import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiSearch, FiArrowRight, FiStar, FiShield, FiClock } from "react-icons/fi";
import Card from "../Components/Card";
import properties, { locations } from "../data/properties";
import image1 from "../Assets/Images/image1.jpg";
import image2 from "../Assets/Images/image2.jpg";
import image3 from "../Assets/Images/image3.jpg";
import image5 from "../Assets/Images/image5.jpg";

function useTypewriter(text, speed = 50, delay = 400) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    let i = 0;
    setDisplayed("");
    setDone(false);

    const startTimeout = setTimeout(() => {
      const interval = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(interval);
          setDone(true);
        }
      }, speed);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [text, speed, delay]);

  return { displayed, done };
}

export default function Home() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const navigate = useNavigate();

  const heroText = "Discover Your Perfect Home in Kigali";
  const { displayed, done } = useTypewriter(heroText, 45, 500);

  const featured = properties.filter((p) => p.featured).slice(0, 4);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set("search", searchQuery);
    if (selectedLocation) params.set("location", selectedLocation);
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <div className="page-wrapper">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <div className="fade-in-up">
                <span className="badge bg-primary text-white fw-semibold px-3 py-2 rounded-pill mb-3">
                  🏠 #1 Real Estate Platform in Kigali
                </span>
                <h1 className="hero-title">
                  {done ? (
                    <>Discover Your <span className="highlight">Perfect Home</span> in Kigali</>
                  ) : (
                    <span className="typewriter-text">
                      {displayed}
                      <span className="typewriter-cursor">|</span>
                    </span>
                  )}
                </h1>
                <p className="hero-subtitle">
                  Browse premium properties across Kigali's most desirable neighborhoods.
                  From modern villas to cozy apartments, find the home that matches your lifestyle.
                </p>
              </div>

              {/* Search Bar */}
              <form className="hero-search fade-in-up-delay-1" onSubmit={handleSearch}>
                <div className="row g-2">
                  <div className="col-md-5">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search properties..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <select
                      className="form-select"
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                    >
                      <option value="">All Locations</option>
                      {locations.slice(1).map((loc) => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <button className="btn btn-search w-100" type="submit">
                      <FiSearch className="me-1" /> Search
                    </button>
                  </div>
                </div>
              </form>

              <div className="hero-stats fade-in-up-delay-2">
                <div className="hero-stat-item">
                  <h3>{properties.length}+</h3>
                  <p>Properties Listed</p>
                </div>
                <div className="hero-stat-item">
                  <h3>200+</h3>
                  <p>Happy Clients</p>
                </div>
                <div className="hero-stat-item">
                  <h3>15+</h3>
                  <p>Neighborhoods</p>
                </div>
              </div>
            </div>

            <div className="col-lg-5 d-none d-lg-block">
              <div className="hero-image-grid fade-in-up-delay-3">
                <img src={image1} alt="Property 1" />
                <img src={image2} alt="Property 2" />
                <img src={image5} alt="Property 3" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-2" style={{ color: "var(--secondary)" }}>
              Why Choose Home Discovery?
            </h2>
            <p className="text-muted">We make finding your dream home simple, fast, and enjoyable</p>
          </div>
          <div className="row g-4">
            <div className="col-md-4">
              <div className="stat-card text-center p-4 fade-in-up">
                <div className="stat-icon blue mx-auto mb-3">
                  <FiStar />
                </div>
                <h5 className="fw-bold">Premium Listings</h5>
                <p className="text-muted small mb-0">
                  Handpicked premium properties verified by our expert team to ensure quality and value.
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="stat-card text-center p-4 fade-in-up-delay-1">
                <div className="stat-icon green mx-auto mb-3">
                  <FiShield />
                </div>
                <h5 className="fw-bold">Trusted Agents</h5>
                <p className="text-muted small mb-0">
                  Work with licensed, professional agents who have deep knowledge of the Kigali market.
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="stat-card text-center p-4 fade-in-up-delay-2">
                <div className="stat-icon amber mx-auto mb-3">
                  <FiClock />
                </div>
                <h5 className="fw-bold">Quick Process</h5>
                <p className="text-muted small mb-0">
                  From search to sale, our streamlined process saves you time and simplifies home buying.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-5" style={{ background: "var(--gray-50)" }}>
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Featured Properties</h2>
              <p className="section-subtitle">Discover our top picks for you</p>
            </div>
            <Link
              to="/properties"
              className="btn btn-outline-primary rounded-pill px-4"
            >
              View All <FiArrowRight className="ms-1" />
            </Link>
          </div>
          <div className="row g-4">
            {featured.map((property) => (
              <div className="col-md-6 col-lg-3" key={property.id}>
                <Card property={property} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-5">
        <div className="container">
          <div
            className="rounded-4 p-5 text-center text-white position-relative overflow-hidden"
            style={{ background: "var(--gradient-primary)" }}
          >
            <div className="position-relative" style={{ zIndex: 1 }}>
              <h2 className="fw-bold mb-3">Ready to Find Your Dream Home?</h2>
              <p className="mb-4 opacity-75" style={{ maxWidth: 500, margin: "0 auto" }}>
                Create a free account to save your favorite properties, get personalized
                recommendations, and access your dashboard.
              </p>
              <div className="d-flex justify-content-center gap-3">
                <Link to="/sign-up" className="btn btn-light fw-semibold px-4 py-2 rounded-pill">
                  Get Started Free
                </Link>
                <Link to="/properties" className="btn btn-outline-light fw-semibold px-4 py-2 rounded-pill">
                  Browse Properties
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
